import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, CaretDoubleRight, CaretDoubleLeft, Pulse, ChatCircleDots, ThumbsUp } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNateM } from "../../context/NateMContext";
import { useLeadFocus } from "../../context/LeadFocusContext";
import { RelayComposer } from "./RelayComposer";

const LOADING_TEXT = "Nia — connecting systems";
const RELAY_PREFIX = { admin: "/admin", manager: "/manager", affiliate: "/rep" };

const GREETING_BY_ROLE = {
  admin: "Ask me anything across the whole portal — ecommerce, trust, funding, leads, affiliates, managers, and commissions.",
  manager: "Ask me anything about your affiliates, leads, referral links, or commissions — or ask about IGC products to explain them to your team.",
  client: "Ask me anything about your account, store performance, trust/funding status, or referrals.",
  affiliate: "Ask me anything about your referred leads, commissions, or IGC products.",
  backoffice: "Ask me anything about your submitted weekly reports.",
};

const NIA_INTRO_SEEN_KEY = "nia_intro_seen";

const NIA_INTRO_BY_ROLE = {
  admin: "I'm Nia, your always-on assistant for the whole IGC portal. Think of me as a teammate who already knows every lead, deal, manager, affiliate, and dollar in the system — so instead of digging through pages, you can just ask. Try things like \"how many deals closed this week\", \"who's low on leads\", or \"summarize what happened with the funding webhook today\". I'll also proactively ping you here when something needs your attention, like a lead going Unqualified or a commission ready for approval. There's no wrong way to ask — type in plain English and I'll figure out what you mean.",
  manager: "I'm Nia, your personal assistant for everything happening with your team. I already know your affiliates' leads, calls, deals, and commissions, so you don't have to click through every rep's page to find an answer. Ask me things like \"how is my team doing this week\", \"which of my reps needs more leads\", or \"explain what a Trust deal referral pays out\" and I'll answer using your real, live data. I'll also send you a quick heads-up here whenever something on your team changes that you should know about — like a deal moving stages or a rep running low on leads. Just type your question below like you're texting a coworker.",
  affiliate: "I'm Nia, your personal coach and assistant while you work your leads. I can see everything on your account — your assigned leads, your call history, your deals, and your commissions — so you can ask me real questions instead of guessing. Try \"how much have I earned this month\", \"what should I say if a lead says they're not interested\", or \"explain how Business Funding commission works\". If you have a lead card open, I already know which one — no need to tell me. I'll also check in with you proactively if a deal you're working changes status, so keep an eye on this panel.",
  client: "I'm Nia, your assistant for anything about your IGC account. I can see your store's performance, your Trust or Funding status, and your referral activity, so feel free to ask me things like \"how is my store doing this month\", \"what stage is my funding application at\", or \"how do I refer a friend and earn a commission\". No need to dig through every tab — just ask me in plain English and I'll pull the real answer from your account.",
  backoffice: "I'm Nia, your assistant for your weekly store reports. I can see everything you've submitted, so ask me things like \"what did I report for this store last week\" or \"which reports are still pending review\" and I'll answer using your real submission history.",
};

const NIA_INTRO_TITLE = "Meet Nia";

export function NateMWidget() {
  const { user } = useAuth();
  const { minimized, setMinimized, narrow, w9Gated } = useNateM();
  const { focusedLead } = useLeadFocus();
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const scrollRef = useRef(null);
  const markedViewedRef = useRef(false);

  useEffect(() => {
    if (!user?.id || minimized) return;
    const seenKey = `${NIA_INTRO_SEEN_KEY}_${user.id}`;
    if (!localStorage.getItem(seenKey)) setShowIntro(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, minimized]);

  const dismissIntro = () => {
    if (user?.id) localStorage.setItem(`${NIA_INTRO_SEEN_KEY}_${user.id}`, "true");
    setShowIntro(false);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const prefix = RELAY_PREFIX[user?.role];
    if (!prefix) return;
    const poll = () => {
      api.get(`${prefix}/relay/inbox`).then(({ data }) => {
        if (!data.length) return;
        setMessages((prev) => [
          ...prev,
          ...data.map((d) => ({ role: "nate", relay: true, relayId: d.id, fromName: d.from_name, canReply: d.can_reply, text: d.nia_text })),
        ]);
        if (minimized) setHasUpdate(true);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 12000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const reactToRelay = (i, relayId) => {
    const prefix = RELAY_PREFIX[user?.role];
    if (!prefix) return;
    api.post(`${prefix}/relay/${relayId}/react`)
      .then(() => setMessages((prev) => prev.map((m, idx) => (idx === i ? { ...m, reacted: true } : m))))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  const [proactivePreview, setProactivePreview] = useState("");
  const sinceRef = useRef(null);

  useEffect(() => {
    const prefix = RELAY_PREFIX[user?.role];
    if (!prefix) return;
    if (sinceRef.current === null) sinceRef.current = new Date().toISOString();
    // Nia's proactive feed (Sep 19 2026) — she speaks up on her own here: lead-card briefs, deal
    // status updates, team nudges, milestones. Silent background poll — never disruptive, never
    // duplicated into the notification bell.
    const poll = () => {
      api.get(`${prefix}/nate-proactive-feed?since=${encodeURIComponent(sinceRef.current)}`).then(({ data }) => {
        if (!data.length) return;
        sinceRef.current = data[data.length - 1].created_at;
        setMessages((prev) => [...prev, ...data.map((d) => ({ role: "nate", proactive: true, text: d.message }))]);
        setProactivePreview(data[data.length - 1].message);
        if (minimized) setHasUpdate(true);
      }).catch(() => {});
    };
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, minimized]);

  useEffect(() => {
    if (!user?.role) return;
    if (user.role === "manager") {
      setMessages([{ role: "nate", text: LOADING_TEXT }]);
      api.get("/manager/nate-proactive-brief")
        .then(({ data }) => {
          setMessages([{ role: "nate", text: data.text }]);
          setHasUpdate(!!data.has_updates);
          setQuickReplies(data.quick_replies || []);
        })
        .catch(() => setMessages([{ role: "nate", text: GREETING_BY_ROLE.manager }]));
      return;
    }
    if (user.role === "affiliate") {
      setMessages([{ role: "nate", text: LOADING_TEXT }]);
      api.get("/rep/nate-proactive-brief")
        .then(({ data }) => {
          setMessages([{ role: "nate", text: data.text }]);
          setQuickReplies(data.quick_replies || []);
          setHasUpdate(!!data.has_updates);
        })
        .catch(() => setMessages([{ role: "nate", text: GREETING_BY_ROLE.affiliate }]));
      return;
    }
    if (user.role !== "admin") {
      setMessages([{ role: "nate", text: GREETING_BY_ROLE[user.role] || "Ask me anything." }]);
      return;
    }
    setMessages([{ role: "nate", text: LOADING_TEXT }]);
    api.get("/admin/nate-trulli/proactive-brief")
      .then(({ data }) => {
        setMessages([{ role: "nate", text: data.text }]);
        setHasUpdate(!!data.has_updates);
        if (!markedViewedRef.current) {
          markedViewedRef.current = true;
          api.post("/admin/nate-trulli/mark-brief-viewed").catch(() => {});
        }
      })
      .catch(() => setMessages([{ role: "nate", text: LOADING_TEXT }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const ask = async (q) => {
    const finalQuestion = (q ?? question).trim();
    if (!finalQuestion || asking) return;
    setQuickReplies([]);
    setMessages((prev) => [...prev, { role: "user", text: finalQuestion }]);
    setQuestion("");
    setAsking(true);
    try {
      // Neo Prompt (Sep 18 2026): automatic context handoff — whatever lead card this rep has
      // open rides along on every question, no button, no explaining it to Nia themselves.
      const { data } = await api.post("/nate/ask", {
        question: finalQuestion,
        lead_id: user?.role === "affiliate" ? focusedLead?.id : undefined,
      });
      setMessages((prev) => [...prev, { role: "nate", text: data.answer }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setMessages((prev) => [...prev, { role: "nate", text: "I couldn't reach Nia just now, try again in a moment." }]);
    } finally {
      setAsking(false);
    }
  };

  if (!user?.role || (user?.must_change_password && !user?._impersonating) || w9Gated) return null;
  if (!GREETING_BY_ROLE[user.role] && user.role !== "admin") return null;

  if (minimized) {
    // New Build 1 (Sep 18 2026): a small pinned toggle only — no leaky full-height strip. Fixed
    // positioning means it floats over the page and never reserves layout width (see
    // NATE_M_MINIMIZED_WIDTH = 0 in NateMContext).
    return (
      <div className="fixed top-1/2 -translate-y-1/2 right-0 z-40" data-testid="nate-m-sidebar-minimized">
        <button
          data-testid="nate-m-expand-button"
          onClick={() => { setMinimized(false); setHasUpdate(false); }}
          disabled={narrow}
          title={narrow ? "Widen your window to expand Nia" : (hasUpdate ? proactivePreview || "Nia has something for you" : "Expand Nia")}
          className={`relative flex items-center justify-center w-6 h-16 rounded-l-lg border border-r-0 transition-colors ${
            narrow
              ? "bg-igc-surface/70 border-igc-gold/10 text-igc-muted/40 cursor-not-allowed"
              : "bg-igc-surface border-igc-gold/25 text-igc-goldText hover:bg-igc-gold/10 hover:text-igc-cream"
          }`}
        >
          <CaretDoubleLeft size={14} weight="bold" />
          {hasUpdate && (
            <span
              className="absolute -top-1.5 -left-1.5 w-3.5 h-3.5 rounded-full bg-igc-success animate-pulse shadow-[0_0_10px_3px_rgba(34,197,94,0.75)]"
              data-testid="nate-m-update-dot-mini"
              title={proactivePreview || "Nia has something for you"}
            />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed top-0 right-0 h-screen w-[360px] border-l border-igc-gold/15 bg-igc-surface z-40 flex flex-col" data-testid="nate-m-sidebar">
      {showIntro && (
        <div className="absolute inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="nia-intro-overlay">
          <div className="bg-igc-surface border border-igc-gold/30 p-5 max-h-full overflow-y-auto" data-testid="nia-intro-card">
            <div className="flex items-center gap-2 mb-3">
              <Pulse size={18} weight="fill" className="text-igc-success" />
              <p className="text-igc-cream font-serif text-lg" data-testid="nia-intro-title">{NIA_INTRO_TITLE}</p>
            </div>
            <p className="text-igc-cream text-sm leading-relaxed whitespace-pre-wrap mb-5" data-testid="nia-intro-body">
              {NIA_INTRO_BY_ROLE[user.role] || NIA_INTRO_BY_ROLE.affiliate}
            </p>
            <button
              data-testid="nia-intro-dismiss-button"
              onClick={dismissIntro}
              title="Closes this welcome message — you can always start typing below whenever you have a question for Nia."
              className="w-full h-10 bg-igc-gold hover:bg-igc-goldHover text-black font-medium transition-colors"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between px-4 py-4 border-b border-igc-gold/15 shrink-0">
        <div className="flex items-center gap-2">
          <Pulse size={16} weight="fill" className="text-igc-success" />
          <div>
            <p className="text-igc-cream font-serif text-base leading-none">Nia</p>
            <p className="text-igc-muted text-[11px] mt-0.5">Your IGC intelligence assistant</p>
          </div>
        </div>
        <button data-testid="nate-m-minimize-button" onClick={() => setMinimized(true)} className="text-igc-muted hover:text-igc-goldText transition-colors" title="Minimize">
          <CaretDoubleRight size={18} weight="bold" />
        </button>
      </div>

      {user.role === "affiliate" && focusedLead && (
        <div className="px-4 py-2 border-b border-igc-gold/10 shrink-0 bg-igc-gold/5" data-testid="nate-m-active-lead-banner">
          <p className="text-igc-goldText/90 text-[11px]">Viewing: <span className="text-igc-cream font-medium">{focusedLead.name}</span> — Nia already has this lead's context</p>
        </div>
      )}

      {RELAY_PREFIX[user.role] && (
        <div className="px-4 py-2 border-b border-igc-gold/10 shrink-0">
          <button
            data-testid="nate-m-new-message-button"
            onClick={() => { setReplyTarget(null); setComposerOpen(true); }}
            title="Sends a direct message to your manager (or team, if you're a manager/admin) — separate from asking Nia a question."
            className="flex items-center gap-1.5 text-[11px] text-igc-goldText/90 hover:text-igc-goldText border border-igc-gold/30 hover:border-igc-gold/60 px-2.5 py-1.5 transition-colors"
          >
            <ChatCircleDots size={13} weight="duotone" /> Send a Message
          </button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" data-testid="nate-m-messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : "text-left"} data-testid={`nate-m-message-${i}`}>
            <div className={`inline-block max-w-[95%] px-3 py-2 text-xs whitespace-pre-wrap text-left ${m.role === "user" ? "bg-igc-gold/15 text-igc-cream" : m.relay ? "bg-igc-gold/10 border border-igc-gold/30 text-igc-cream" : "bg-igc-surfaceLight text-igc-cream"}`}>
              {m.text}
            </div>
            {m.relay && (
              <div className="flex items-center gap-3 mt-1">
                {m.canReply && (
                  <button
                    data-testid={`nate-m-relay-reply-${i}`}
                    onClick={() => { setReplyTarget({ id: m.relayId, from_name: m.fromName }); setComposerOpen(true); }}
                    title="Reply directly to the person who sent this message."
                    className="text-[10px] text-igc-goldText/80 hover:text-igc-goldText underline"
                  >
                    Reply
                  </button>
                )}
                <button
                  data-testid={`nate-m-relay-react-${i}`}
                  disabled={m.reacted}
                  onClick={() => reactToRelay(i, m.relayId)}
                  title="Sends a quick thumbs-up acknowledgment back to the sender without typing a reply."
                  className={`flex items-center gap-1 text-[10px] ${m.reacted ? "text-igc-success" : "text-igc-muted hover:text-igc-goldText"}`}
                >
                  <ThumbsUp size={11} weight={m.reacted ? "fill" : "regular"} /> {m.reacted ? "Reacted" : "React"}
                </button>
              </div>
            )}
          </div>
        ))}
        {asking && <p className="text-igc-muted text-xs" data-testid="nate-m-thinking">{LOADING_TEXT}...</p>}
      </div>

      {quickReplies.length > 0 && !asking && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t border-igc-gold/10 shrink-0" data-testid="nate-m-quick-replies">
          {quickReplies.map((qr, i) => (
            <button
              key={i}
              data-testid={`nate-m-quick-reply-${i}`}
              onClick={() => ask(qr)}
              title="Click to instantly ask Nia this suggested question."
              className="text-[11px] px-2.5 py-1.5 bg-igc-surfaceLight border border-igc-gold/20 text-igc-goldText hover:border-igc-gold/50 hover:bg-igc-gold/10 transition-colors text-left"
            >
              {qr}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2 px-3 py-3 border-t border-igc-gold/15 shrink-0">
        <input
          data-testid="nate-m-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask Nia anything..."
          title="Type any question about leads, deals, commissions, or IGC products — Nia answers using your live account data."
          className="flex-1 bg-igc-surfaceLight border border-igc-gold/20 text-igc-cream text-sm px-3 py-2 outline-none focus:border-igc-gold/50"
        />
        <button type="submit" data-testid="nate-m-send-button" disabled={asking} title="Sends your question to Nia." className="w-9 h-9 flex items-center justify-center bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-50 shrink-0">
          <PaperPlaneTilt size={16} weight="fill" />
        </button>
      </form>
      {RELAY_PREFIX[user.role] && (
        <RelayComposer open={composerOpen} onClose={() => setComposerOpen(false)} replyTo={replyTarget} />
      )}
    </div>
  );
}
